import type { JSX } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

export interface TagMarkProps {
  size?: number;
  dark?: boolean;
  ariaHidden?: boolean;
}

/**
 * TagMark — PRDKT tag mark (the icon — primary brand mark).
 *
 * Ported verbatim from design-reference/predikt-v2/project/site-brand.jsx (BRP-01, D-14..D-17).
 * Changes from original:
 *   1. TypeScript prop types.
 *   2. Hex literals replaced with CSS var tokens (BRP-02).
 *   3. Accessible <title> from i18n (D-15).
 *
 * viewBox: "0 0 200 100" — byte-identical to design source (D-17).
 * Default size=80: width = size * 2, height = size.
 */
export function TagMark({
  size = 80,
  dark = false,
  ariaHidden = false,
}: TagMarkProps): JSX.Element {
  const { t } = useTranslation();
  const titleId = useId();
  const title = t('brand.tagMark.title');

  // Token-driven fills. NEVER hex literals.
  const fg = dark ? 'var(--color-paper)' : 'var(--color-ink)';
  const bg = dark ? 'var(--color-ink)' : 'var(--color-paper)';
  const accent = 'var(--color-orange)';

  const a11yProps = ariaHidden
    ? ({ 'aria-hidden': true } as const)
    : ({ role: 'img', 'aria-labelledby': titleId } as const);

  return (
    <svg
      viewBox="0 0 200 100"
      style={{ width: size * 2, height: size, display: 'block' }}
      {...a11yProps}
    >
      {!ariaHidden && <title id={titleId}>{title}</title>}
      <polygon points="4,8 150,8 196,50 150,92 4,92" fill={fg} />
      <circle cx="34" cy="50" r="9" fill={accent} />
      <text
        x="56"
        y="60"
        fontFamily="var(--font-mono)"
        fontSize="28"
        fontWeight="700"
        fill={bg}
        letterSpacing="3"
      >
        PRDKT
      </text>
    </svg>
  );
}

export default TagMark;
